import { CellClickEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { GroupDescriptor, GroupResult, process, SortDescriptor, State } from '@progress/kendo-data-query';

/**
 * Grouping on the Admin grids always runs in the browser. The list APIs page through Kendo.Mvc's
 * ToDataSourceResult, and several of them reshape that page afterwards (or the grid groups on values
 * that only exist after the reshaping, such as a client's Status), so a grouped request would not
 * come back in a usable shape - group descriptors are never sent to the server.
 *
 * A server-paged grid that is grouped loads every row matching its current search and filters
 * instead of a single page, then pages through the groups locally, so each group covers the whole
 * list rather than the rows that happened to land on one page.
 */

/** Largest page size the APIs' `int PageSize` accepts - one page then holds every matching row. */
export const ALL_ROWS_PAGE_SIZE = 2147483647;

export function isGrouped(state: State): boolean {
  return !!state.group?.length;
}

/** The grid state without its grouping - what a list API is sent. */
export function withoutGroups(state: State): State {
  return { ...state, group: [] };
}

/** The request state for a server-paged grid: its current page, or every row while it is grouped. */
export function toServerState(state: State): State {
  return isGrouped(state)
    ? { ...state, skip: 0, take: ALL_ROWS_PAGE_SIZE, group: [] }
    : withoutGroups(state);
}

/**
 * The grid data for a list API response fetched with toServerState(): the page as returned, or,
 * while grouped, the current page of the grouped full list.
 */
export function toGridData(rows: any[], total: number, state: State): GridDataResult {
  return isGrouped(state)
    ? process(rows, { skip: state.skip, take: state.take, group: state.group })
    : { data: rows, total };
}

/** Groups only the rows of the current server page - for lists too large to load in full. */
export function groupCurrentPage(rows: any[], total: number, state: State): GridDataResult {
  return {
    data: isGrouped(state) ? process(rows, { group: state.group }).data : rows,
    total,
  };
}

/** The data rows behind a grid's data, in display order - grouped data nests one level per group. */
export function dataRows(data: any[], groups: GroupDescriptor[] | undefined): any[] {
  let rows = data;
  for (let level = 0; level < (groups?.length ?? 0); level++) {
    rows = rows.flatMap((group: GroupResult) => group.items);
  }
  return rows;
}

/** Sort descriptors that put rows with the same group values next to each other. */
export function groupSort(groups: GroupDescriptor[] | undefined): SortDescriptor[] {
  return (groups ?? []).map(group => ({ field: group.field, dir: group.dir ?? 'asc' }));
}

/**
 * True for the second click of a double-click on a grid row. Handle row double-clicks through the
 * grid's (cellClick) and its `dataItem` rather than a (dblclick) on the row's position in the table:
 * once the grid is grouped, group header rows sit between the data rows and a position no longer
 * lines up with the grid's data.
 */
export function isRowDoubleClick(event: CellClickEvent): boolean {
  return event.originalEvent?.detail === 2;
}
